import React, { useState, useEffect, useMemo } from 'react';
import { api, API } from '../App';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Package, Calendar, Trash2, Edit, Eye, Filter, Download, Truck, BarChart2 } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';

export default function Purchases() {
  const { isAdmin } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [viewPurchase, setViewPurchase] = useState(null);
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    supplier_id: '',
    batch_no: '',
    factory: '',
    kg_received: '',
    total_cost: '',
    purchase_type: 'Maize',
    commodity_name: '',
    quantity: '',
    unit: 'pieces',
    remarks: ''
  });

  // Filter state
  const [filterStart, setFilterStart] = useState(null);
  const [filterEnd, setFilterEnd] = useState(null);
  const [filterSupplierId, setFilterSupplierId] = useState('all');
  const [filterWeight, setFilterWeight] = useState('all'); // all | under_25k | 25k_and_above
  const [filterType, setFilterType] = useState('all'); // all | Maize | Commodity
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, suppliersRes] = await Promise.all([
        api.get('/purchases'),
        api.get('/suppliers')
      ]);
      // Sort purchases by date DESC (newest first)
      const sortedPurchases = purchasesRes.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setPurchases(sortedPurchases);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCommodity = formData.purchase_type === 'Commodity';
    if (isCommodity) {
      if (!formData.supplier_id || !formData.commodity_name || !formData.quantity || !formData.total_cost) {
        toast.error('Please fill supplier, commodity, quantity and total cost');
        return;
      }
    } else if (!formData.supplier_id || !formData.batch_no || !formData.factory || !formData.kg_received || !formData.total_cost) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const payload = isCommodity ? {
        date: format(date, 'yyyy-MM-dd'),
        supplier_id: formData.supplier_id,
        factory: formData.factory || null,
        total_cost: parseFloat(formData.total_cost),
        paid_amount: 0,
        purchase_type: 'Commodity',
        commodity_name: formData.commodity_name.trim(),
        quantity: parseFloat(formData.quantity),
        unit: formData.unit || 'pieces',
        remarks: formData.remarks || null,
      } : {
        date: format(date, 'yyyy-MM-dd'),
        supplier_id: formData.supplier_id,
        batch_no: formData.batch_no.trim().toUpperCase(),
        factory: formData.factory,
        kg_received: parseFloat(formData.kg_received),
        total_cost: parseFloat(formData.total_cost),
        paid_amount: 0,
        purchase_type: 'Maize',
      };

      if (editingPurchase) {
        await api.put(`/purchases/${editingPurchase.id}`, payload);
        toast.success('Purchase updated');
      } else {
        await api.post('/purchases', payload);
        toast.success('Purchase recorded');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save purchase');
    }
  };

  const resetForm = () => {
    setFormData({ supplier_id: '', batch_no: '', factory: '', kg_received: '', total_cost: '', purchase_type: 'Maize', commodity_name: '', quantity: '', unit: 'pieces', remarks: '' });
    setDate(new Date());
    setEditingPurchase(null);
  };

  const openEditDialog = (purchase) => {
    setEditingPurchase(purchase);
    setDate(new Date(purchase.date));
    setFormData({
      supplier_id: purchase.supplier_id,
      batch_no: purchase.batch_no || '',
      factory: purchase.factory || '',
      kg_received: (purchase.kg_received || 0).toString(),
      total_cost: purchase.total_cost.toString(),
      purchase_type: purchase.purchase_type || 'Maize',
      commodity_name: purchase.commodity_name || '',
      quantity: purchase.quantity != null ? purchase.quantity.toString() : '',
      unit: purchase.unit || 'pieces',
      remarks: purchase.remarks || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this purchase? This will remove all related inventory.')) return;
    try {
      await api.delete(`/purchases/${id}`);
      toast.success('Purchase deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const openDetailsDialog = (purchase) => {
    setViewPurchase(purchase);
    setDetailsDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getBatchStatusBadge = (status) => {
    const variants = {
      'Raw': { class: 'bg-amber-100 text-amber-700', label: 'Raw' },
      'Processed': { class: 'bg-green-100 text-green-700', label: 'Processed' }
    };
    const config = variants[status] || variants['Raw'];
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  // Calculate total cost for summary (over filtered set)
  const filteredPurchases = useMemo(() => {
    return purchases.filter(p => {
      if (filterStart && p.date < format(filterStart, 'yyyy-MM-dd')) return false;
      if (filterEnd && p.date > format(filterEnd, 'yyyy-MM-dd')) return false;
      if (filterSupplierId !== 'all' && p.supplier_id !== filterSupplierId) return false;
      if (filterType !== 'all' && (p.purchase_type || 'Maize') !== filterType) return false;
      const kg = p.kg_received || 0;
      if (filterWeight === 'under_25k' && kg >= 25000) return false;
      if (filterWeight === '25k_and_above' && kg < 25000) return false;
      return true;
    });
  }, [purchases, filterStart, filterEnd, filterSupplierId, filterWeight, filterType]);

  const totalCost = filteredPurchases.reduce((sum, p) => sum + (p.total_cost || 0), 0);
  const totalKg = filteredPurchases.reduce((sum, p) => sum + (p.kg_received || 0), 0);
  const rawCount = filteredPurchases.filter(p => p.batch_status === 'Raw').length;

  const clearFilters = () => {
    setFilterStart(null);
    setFilterEnd(null);
    setFilterSupplierId('all');
    setFilterWeight('all');
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filterStart) params.append('start_date', format(filterStart, 'yyyy-MM-dd'));
      if (filterEnd) params.append('end_date', format(filterEnd, 'yyyy-MM-dd'));
      if (filterSupplierId !== 'all') params.append('supplier_id', filterSupplierId);
      if (filterWeight !== 'all') params.append('weight_bracket', filterWeight);
      const url = `${API}/purchases/report/pdf${params.toString() ? '?' + params.toString() : ''}`;
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `Purchases_${format(new Date(), 'yyyyMMdd')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dl);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchases-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Purchases</h1>
          <p className="text-slate-500 mt-1">Maize procurement records with batch tracking</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="gap-2 flex-1 sm:flex-none" onClick={handleDownloadPDF} disabled={downloading} data-testid="download-purchases-pdf">
            <Download className="h-4 w-4" /> {downloading ? 'Downloading…' : 'Download PDF'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2 flex-1 sm:flex-none" data-testid="add-purchase-btn">
                <Plus className="h-4 w-4" />
                Add Purchase
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">{editingPurchase ? 'Edit Purchase' : 'Record Purchase'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Purchase Type *</Label>
                <Select value={formData.purchase_type} onValueChange={(v) => setFormData({ ...formData, purchase_type: v })}>
                  <SelectTrigger data-testid="purchase-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Maize">Maize</SelectItem>
                    <SelectItem value="Commodity">Commodity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <DatePickerField date={date} onDateChange={setDate} />
              </div>
              {formData.purchase_type === 'Maize' && (
                <div className="space-y-2">
                  <Label>Batch No *</Label>
                  <Input
                    value={formData.batch_no}
                    onChange={(e) => setFormData({ ...formData, batch_no: e.target.value })}
                    placeholder="e.g., B001"
                    data-testid="purchase-batch-input"
                  />
                  <p className="text-xs text-slate-500">Unique identifier for this batch</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Supplier *</Label>
                <Select value={formData.supplier_id} onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}>
                  <SelectTrigger data-testid="purchase-supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Factory {formData.purchase_type === 'Maize' ? '*' : ''}</Label>
                <Select value={formData.factory} onValueChange={(v) => setFormData({ ...formData, factory: v })}>
                  <SelectTrigger data-testid="purchase-factory-select">
                    <SelectValue placeholder="Select factory" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                    <SelectItem value="Githunguri">Githunguri</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.purchase_type === 'Commodity' ? (
                <>
                  <div className="space-y-2">
                    <Label>Commodity / Item *</Label>
                    <Input
                      value={formData.commodity_name}
                      onChange={(e) => setFormData({ ...formData, commodity_name: e.target.value })}
                      placeholder="e.g., Bailers, Packaging Paper, Bags"
                      data-testid="purchase-commodity-name"
                    />
                    <p className="text-xs text-slate-500">Same commodity across purchases is automatically reused</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Quantity *</Label>
                      <Input type="number" step="0.01" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="0" data-testid="purchase-commodity-qty" />
                    </div>
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Input value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} placeholder="pieces / rolls / kg" data-testid="purchase-commodity-unit" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Total Amount (KES) *</Label>
                    <Input type="number" step="0.01" value={formData.total_cost} onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })} placeholder="0" data-testid="purchase-cost-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Remarks</Label>
                    <Input value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} placeholder="optional" />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Kg Received *</Label>
                    <Input
                      type="number"
                      value={formData.kg_received}
                      onChange={(e) => setFormData({ ...formData, kg_received: e.target.value })}
                      placeholder="0"
                      data-testid="purchase-kg-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Cost (KES) *</Label>
                    <Input
                      type="number"
                      value={formData.total_cost}
                      onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })}
                      placeholder="0"
                      data-testid="purchase-cost-input"
                    />
                  </div>
                </div>
              )}
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                Note: Payments to suppliers are now recorded directly from the Suppliers page.
              </p>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="purchase-submit-btn">
                  {editingPurchase ? 'Update Purchase' : 'Record Purchase'}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5"><Filter className="h-4 w-4 text-slate-500" /></div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">From:</Label>
              <DatePickerField date={filterStart} onDateChange={setFilterStart} buttonClassName="w-36 text-sm" displayFormat="MMM d, yyyy" placeholder="Start" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">To:</Label>
              <DatePickerField date={filterEnd} onDateChange={setFilterEnd} buttonClassName="w-36 text-sm" displayFormat="MMM d, yyyy" placeholder="End" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Supplier:</Label>
              <Select value={filterSupplierId} onValueChange={setFilterSupplierId}>
                <SelectTrigger className="w-44 text-sm" data-testid="filter-supplier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Type:</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32 text-sm" data-testid="filter-purchase-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Maize">Maize</SelectItem>
                  <SelectItem value="Commodity">Commodity</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs"><Truck className="h-3 w-3 inline mr-1" />Weight:</Label>
              <Select value={filterWeight} onValueChange={setFilterWeight}>
                <SelectTrigger className="w-44 text-sm" data-testid="filter-weight"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="under_25k">Under 25,000 kg (Lorries)</SelectItem>
                  <SelectItem value="25k_and_above">25,000 kg+ (Trailers)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(filterStart || filterEnd || filterSupplierId !== 'all' || filterWeight !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
            )}
            <div className="ml-auto text-xs text-slate-500">
              Showing {filteredPurchases.length} of {purchases.length}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Batches</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="summary-batches">{filteredPurchases.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Package className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Raw Batches</p>
                <p className="text-2xl font-bold text-amber-600">
                  {rawCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Kg</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="summary-kg">
                  {new Intl.NumberFormat('en-KE').format(totalKg)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Package className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Cost</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="summary-cost">
                  {formatCurrency(totalCost)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Batch No</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Factory</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Kg Received</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPurchases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      {purchases.length === 0 ? 'No purchases recorded' : 'No purchases match the active filters'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPurchases.map((purchase) => (
                    <TableRow key={purchase.id} className="table-row" data-testid={`purchase-row-${purchase.id}`}>
                      <TableCell className="font-bold text-[#1B4D3E]">
                        {purchase.purchase_type === 'Commodity' ? (
                          <span className="text-blue-700">{purchase.commodity_name || '—'}</span>
                        ) : (
                          purchase.batch_no
                        )}
                        {purchase.purchase_type === 'Commodity' && <Badge variant="outline" className="ml-1 text-[10px]">Commodity</Badge>}
                      </TableCell>
                      <TableCell>{purchase.date}</TableCell>
                      <TableCell className="font-medium">{purchase.supplier_name}</TableCell>
                      <TableCell>
                        {purchase.factory ? (
                          <Badge variant="outline">{purchase.factory}</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {purchase.purchase_type === 'Commodity' ? (
                          <Badge variant="outline" className="text-xs">{purchase.quantity} {purchase.unit || ''}</Badge>
                        ) : (
                          getBatchStatusBadge(purchase.batch_status)
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {purchase.purchase_type === 'Commodity' ? '—' : new Intl.NumberFormat('en-KE').format(purchase.kg_received)}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(purchase.total_cost)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {purchase.purchase_type !== 'Commodity' && (
                            <RouterLink to={`/batches/${purchase.id}`}>
                              <Button variant="ghost" size="sm" title="Open Batch Report" data-testid={`open-batch-report-${purchase.id}`}>
                                <BarChart2 className="h-4 w-4 text-emerald-700" />
                              </Button>
                            </RouterLink>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetailsDialog(purchase)}
                            title="View Details"
                            data-testid={`view-purchase-${purchase.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(purchase)}
                            title="Edit"
                            data-testid={`edit-purchase-${purchase.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(purchase.id)}
                              data-testid={`delete-purchase-${purchase.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Purchase Details - {viewPurchase?.batch_no}
            </DialogTitle>
          </DialogHeader>
          {viewPurchase && (
            <div className="space-y-4">
              {/* Purchase Info */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Date</p>
                    <p className="font-medium">{viewPurchase.date}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Supplier</p>
                    <p className="font-medium">{viewPurchase.supplier_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Factory</p>
                    <p className="font-medium">{viewPurchase.factory || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <p>{getBatchStatusBadge(viewPurchase.batch_status)}</p>
                  </div>
                </div>
                <div className="pt-3 border-t grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Kg Received</p>
                    <p className="text-xl font-bold">{new Intl.NumberFormat('en-KE').format(viewPurchase.kg_received)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Cost</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(viewPurchase.total_cost)}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                Supplier payments are now managed from the <strong>Suppliers</strong> page. Go to Suppliers &rarr; Record Payment to pay this supplier.
              </p>

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
